import { CARD_TYPE, COMPONENT_TYPE as TYPE } from 'common/constants';
import type { BBModel, CardFloatRoot, CardData, ComponentTypes } from 'types/story';
import { hasCardFeature } from 'utils/facades/utils';

type CardPlacementComponent = Pick<BBModel, 'type' | 'children'> | Pick<CardFloatRoot, 'type' | 'children'>;

const CARD_ROOT_TYPES = new Set<ComponentTypes>([TYPE.CARD, TYPE.CONTENT, TYPE.FLOAT_BELOW, TYPE.FLOAT_ABOVE]);
const RESULT_TYPES = new Set<ComponentTypes>([TYPE.RESULT_SHAPE, TYPE.RESULT_TEXT]);

export const DEFAULT_BLOCK_ITEMS_LIST: ComponentTypes[] = [
	TYPE.BOX,
	TYPE.TEXT,
	TYPE.BUTTON,
	TYPE.IMG,
	TYPE.ICON,
	TYPE.VIDEO,
	TYPE.LOTTIE,
	TYPE.TIMER,
	TYPE.SHAPE,
	TYPE.EMBED,
	TYPE.INFOGRAPHIC,
	TYPE.AD_UNIT,
	TYPE.SHARE,
	TYPE.OVERLAY,
	TYPE.COUNTER,
	TYPE.SLIDER,
	TYPE.SLIDER_ARROW,
	TYPE.SLIDER_SLIDES,
	TYPE.SLIDER_PAGINATION,
	TYPE.VISUAL_COMPARE,
];

export const ADD_BLOCK_LIST: Record<CardData['type'], ComponentTypes[]> = {
	[CARD_TYPE.SANDBOX]: Object.values(TYPE).filter(type => !CARD_ROOT_TYPES.has(type) && !RESULT_TYPES.has(type)),
	[CARD_TYPE.INFO]: [...DEFAULT_BLOCK_ITEMS_LIST, TYPE.SEARCH_FIELD, TYPE.CMS_REPEATER],
	[CARD_TYPE.REGISTRATION]: [...DEFAULT_BLOCK_ITEMS_LIST, TYPE.GOOGLE_LOGIN, TYPE.SMS_LOGIN],
	[CARD_TYPE.TRIVIA]: [...DEFAULT_BLOCK_ITEMS_LIST, TYPE.ANSWER, TYPE.ANSWER_INPUT, TYPE.BUTTON_SUBMIT],
	[CARD_TYPE.SORTABLE_TRIVIA]: [...DEFAULT_BLOCK_ITEMS_LIST, TYPE.SORTABLE_BOX, TYPE.ANSWER, TYPE.ANSWER_PROGRESS],
	[CARD_TYPE.SORTABLE_POLL]: [...DEFAULT_BLOCK_ITEMS_LIST, TYPE.SORTABLE_BOX, TYPE.ANSWER, TYPE.BUTTON_SUBMIT],
	[CARD_TYPE.POLL]: [
		...DEFAULT_BLOCK_ITEMS_LIST,
		TYPE.ANSWER,
		TYPE.ANSWER_INPUT,
		TYPE.BUTTON_SUBMIT,
		TYPE.ANSWER_PROGRESS,
	],
	[CARD_TYPE.FORM]: [
		...DEFAULT_BLOCK_ITEMS_LIST,
		TYPE.BUTTON_SUBMIT,
		TYPE.TEXT_FIELD,
		TYPE.TEXT_AREA_FIELD,
		TYPE.CHECKBOX,
		TYPE.SELECT,
		TYPE.FILE_UPLOAD_FIELD,
	],
	[CARD_TYPE.PERSONALITY_TEST]: [...DEFAULT_BLOCK_ITEMS_LIST, TYPE.ANSWER],
	[CARD_TYPE.THIS_OR_THAT]: [...DEFAULT_BLOCK_ITEMS_LIST, TYPE.ANSWER, TYPE.SWIPE],
	[CARD_TYPE.TRUE_OR_FALSE]: [...DEFAULT_BLOCK_ITEMS_LIST, TYPE.ANSWER, TYPE.SWIPE],
	[CARD_TYPE.NAVIGATION]: [...DEFAULT_BLOCK_ITEMS_LIST],
};

/**
 * Checks one existing component against a card's placement rules.
 * Unlike the type-only check, this also accounts for an Answer's result children.
 * It does not walk the component's descendants.
 */
export function isComponentAllowedOnCard(component: CardPlacementComponent, cardType: CardData['type']): boolean {
	const cardHasResults = hasCardFeature(cardType).results;

	if (!isComponentTypeAllowedOnCard(component.type, cardType)) return false;
	if (component.type !== TYPE.ANSWER) return true;

	const answerHasResultChildren =
		Array.isArray(component.children) &&
		component.children.some(child => 'type' in child && RESULT_TYPES.has(child.type));

	return answerHasResultChildren === cardHasResults;
}

/** Checks whether a component type is generally allowed on a card, without inspecting a concrete component. */
export function isComponentTypeAllowedOnCard(type: ComponentTypes, cardType: CardData['type']): boolean {
	return RESULT_TYPES.has(type) ? hasCardFeature(cardType).results : ADD_BLOCK_LIST[cardType].includes(type);
}

/** Lists the card types that accept this existing component, including the Answer result-children rule. */
export function getCardTypesAllowingComponent(component: CardPlacementComponent): CardData['type'][] {
	return (Object.keys(ADD_BLOCK_LIST) as CardData['type'][]).filter(cardType =>
		isComponentAllowedOnCard(component, cardType)
	);
}

/** Lists the card types that generally allow this type, without inspecting a concrete component. */
export function getCardTypesAllowingComponentType(type: ComponentTypes): CardData['type'][] {
	return (Object.keys(ADD_BLOCK_LIST) as CardData['type'][]).filter(cardType =>
		isComponentTypeAllowedOnCard(type, cardType)
	);
}
